import { LoadingIndicator } from './LoadingIndicator';
import { Icon, type IconDefinition } from '@vrplatform/icons';
import type { ThemeColor } from '@vrplatform/theme';
import {
  Box,
  Center,
  type MantineSize,
  type MantineSpacing,
  type StyleProp,
  Text,
  Transition,
  useMantineTheme,
} from '@mantine/core';
import { type ComponentProps, type ReactNode, useMemo } from 'react';
import { Button, IconButton } from './button';

type Props = {
  mounted: boolean;
  type: 'error' | 'warning' | 'info';
  mb?: StyleProp<MantineSpacing>;
  p?: StyleProp<MantineSpacing>;
  mt?: StyleProp<MantineSpacing>;
  color?: ThemeColor;
  icon?: IconDefinition;
  loading?: boolean;
  titleColor?: ThemeColor;
  size?: 'md' | 'sm';
  shadow?: MantineSize | null;
  borderColor?: string;
  messageClassName?: string;
  onClose?: () => void;
} & (
  | {
      children?: undefined;
      title: string;
      message: string;
      action?: {
        label: string;
        icon?: IconDefinition;
        variant?: ComponentProps<typeof Button>['variant'];
        loading?: boolean;
      } & (
        | {
            onClick: () => void;
            href?: never;
            target?: never;
            rel?: never;
          }
        | {
            href: string;
            target?: string;
            rel?: string;
            onClick?: never;
          }
      );
    }
  | {
      children: ReactNode;
      title?: undefined;
      message?: undefined;
      action?: undefined;
    }
);

export const Banner = ({
  mounted,
  children,
  type,
  action,
  message,
  title,
  p,
  mb = 'md',
  mt,
  color,
  icon,
  size = 'md',
  loading,
  titleColor,
  shadow = 'sm',
  borderColor,
  messageClassName,
  onClose,
}: Props) => {
  const themeColor: ThemeColor =
    color ?? (type === 'error' ? 'red' : type === 'info' ? 'blue' : 'orange');
  const { colors } = useMantineTheme();

  const sizes = useMemo(() => {
    return {
      iconSize: size === 'md' ? 30 : 20,
      titleSize: size === 'md' ? 'lg' : 'md',
      messageSize: size === 'md' ? 'sm' : 'xs',
    };
  }, [size]);

  if (action && 'href' in action && typeof action.href !== 'string') {
    throw new Error('Banner link action href must be a string.');
  }

  return (
    <Transition
      mounted={mounted}
      transition="fade"
      duration={400}
      timingFunction="ease"
    >
      {(styles) => (
        <Box
          mb={mb}
          mt={mt}
          style={styles}
          p={p}
          sx={(theme) => ({
            backgroundColor: theme.colors[themeColor][0],
            border: `1px solid ${borderColor ?? (type === 'info' ? theme.colors.neutral[2] : theme.colors[themeColor][3])}`,
            padding: p ? undefined : theme.spacing.md,
            borderRadius: theme.radius.md,
            display: 'flex',
            flexDirection: 'row',
            flexWrap: 'nowrap',
            alignItems: 'center',
            justifyContent: 'flex-start',
            gap: theme.spacing.md,
            boxShadow:
              type === 'info'
                ? undefined
                : shadow
                  ? theme.shadows[shadow]
                  : undefined,
            [`@media (max-width: ${theme.breakpoints.xs})`]: {
              flexDirection: 'column',
              alignItems: 'stretch',
              textAlign: 'center',
            },
          })}
        >
          {children ?? (
            <>
              <Center>
                {loading ? (
                  <LoadingIndicator size="xs" color={themeColor} />
                ) : (
                  <Icon
                    icon={icon ?? 'AlertTriangleIcon'}
                    size={sizes.iconSize}
                    color={(theme) => theme.colors[themeColor][7]}
                  />
                )}
              </Center>
              <Box sx={{ flex: 1 }}>
                {title && (
                  <Text
                    size={sizes.titleSize}
                    fw={500}
                    c={titleColor ?? colors[themeColor][7]}
                  >
                    {title}
                  </Text>
                )}
                <Text
                  className={messageClassName}
                  size={sizes.messageSize}
                  c="neutral"
                  mb={children ? 'xs' : undefined}
                >
                  {message}
                </Text>
              </Box>
              {action && (
                <Button
                  size={size === 'md' ? 'sm' : 'xs'}
                  leftIcon={action.icon ?? 'Edit3Icon'}
                  variant={action.variant}
                  loading={action.loading}
                  {...('href' in action
                    ? {
                        component: 'a' as const,
                        href: action.href as string,
                        target: action.target,
                        rel: action.rel,
                      }
                    : { onClick: action.onClick })}
                >
                  {action.label}
                </Button>
              )}
              {onClose && (
                <IconButton
                  icon="CrossIcon"
                  tooltip="Dismiss"
                  onClick={onClose}
                  sx={{ alignSelf: 'center' }}
                />
              )}
            </>
          )}
        </Box>
      )}
    </Transition>
  );
};
